import { Box, Spinner, VStack } from '@chakra-ui/react';
import type { Conversation } from '@fluenceur/types';

import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/ui/empty-state';
import { getConversationHref } from '@/pages/messaging/conversation-route';
import ConversationItem from './ConversationItem';
import { MessageText, Search } from 'iconoir-react';

interface ConversationListProps {
  conversations: Conversation[];
  searchQuery?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadMoreRootRef?: RefObject<HTMLElement | null>;
  onLoadMore?: () => void;
}

const ConversationList = ({
  conversations,
  searchQuery,
  hasNextPage = false,
  isFetchingNextPage = false,
  loadMoreRootRef,
  onLoadMore,
}: ConversationListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectedConversationId = location.pathname.match(/^\/messages\/([^/]+)$/)?.[1] ?? null;

  const handleSelectConversation = (conversation: Conversation) => {
    if (!location.pathname.includes(`/messages/${conversation.id}`)) {
      navigate(getConversationHref(conversation.id, location.search));
    }
  };

  useEffect(() => {
    if (!hasNextPage || !onLoadMore) return;

    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      {
        root: loadMoreRootRef?.current ?? null,
        rootMargin: '160px 0px',
      }
    );

    observer.observe(loadMoreElement);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, loadMoreRootRef, onLoadMore]);

  if (conversations.length === 0) {
    if ((searchQuery ?? '').trim().length > 0) {
      return (
        <EmptyState
          title={t('messages.search.noResults')}
          description={t('messages.search.noResultsDescription', { query: searchQuery })}
          icon={<Search width={24} height={24} />}
        />
      );
    }
    return (
      <EmptyState
        title={t('messages.noConversations')}
        description={t('messages.noConversationsDescription')}
        icon={<MessageText width={24} height={24} />}
      />
    );
  }

  return (
    <VStack gap="1px" align="stretch" w="full">
      {conversations.map((conversation) => (
        <ConversationItem
          key={conversation.id}
          conversation={conversation}
          isSelected={conversation.id === selectedConversationId}
          onClick={() => handleSelectConversation(conversation)}
        />
      ))}
      {hasNextPage && (
        <Box ref={loadMoreRef} display="flex" justifyContent="center" py={3} minH="40px">
          {isFetchingNextPage && <Spinner size="sm" color="navDark.textSubtle" />}
        </Box>
      )}
    </VStack>
  );
};

export default ConversationList;
